import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import Loading from '../../../components/loading';
import styles from '../Detail/detail.module.scss';
import { hostName, isLocal } from '../../../constants/apiEnv';
import Button from '../../../components/button/button';
import Collapsible from '../../../components/collapsible/Collapsible';
import PulpoField from '../../../pulpo_visualizer/fields';
import SectionsList from '../Detail/SectionList';


function TrainingContentList(props) {
  const { history, match: { params } } = props;
  const [loading, setLoading] = useState(false);
  const [airportData, setAirportData] = useState({});
  const [trainingData, setTrainingData] = useState({});
  const [trainingDraft, setTrainingDraft] = useState({});
  const [message, setMessage] = useState(undefined);
  const [info, setInfo] = useState({});
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    first_name: true,
    last_name: true
  });

  // state for scratch training section
  const sectionId = trainingData?.sections?.length > 0 ? trainingData?.sections[0]?.id : undefined;

  // state for scorm training
  const [contentList, setContentList] = useState({});

  useEffect(() => {
    if (Object.keys(airportData).length === 0) {
      if (message === undefined) {
        getTrainingInfo();
      }
    }
  });

  const getTrainingInfo = async () => {
    try {
      const url = hostName.slice(6);
      const publicUrl = isLocal ? process.env.REACT_APP_BACKEND_PUBLICHOST : `https://apis.${params?.backendId || 'us'}${url}/public`;
      setLoading(true);
      await fetch(`${publicUrl}/lms/training/${params.airportCode.toUpperCase()}/${params.trainingId}/`)
        .then(response => response.json())
        .then(async (data) => {
          if (data?.detail) {
            setMessage(data?.detail);
          } else {
            setAirportData(data.airport_data);
            setTrainingData(data);
            if (data?.training_content_type === 'scorm') {
              await fetch(`${publicUrl}/lms/scorm_content_list/${params.airportCode.toUpperCase()}/${params.trainingId}/`)
                .then(response => response.json())
                .then((scormData) => {
                  setContentList(scormData);
                });
            }
          }
          setLoading(false);
        });

      // manage public training draft information
      await fetch(`${publicUrl}/lms/public_training_draft/${params.airportCode.toUpperCase()}/${params.trainingId}/`)
        .then(response => response.json())
        .then((draftData) => {
          setTrainingDraft(draftData);
        });
    } catch (error) {
      // nothing
    }
  };

  const saveTrainingDraft = async (payload) => {
    const url = hostName.slice(6);
    const publicUrl = isLocal ? process.env.REACT_APP_BACKEND_PUBLICHOST : `https://apis.${params?.backendId || 'us'}${url}/public`;
    setLoading(true);
    try {
      const res = await fetch(`${publicUrl}/lms/public_training_draft/${params.airportCode.toUpperCase()}/${params.trainingId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Error: ${res.statusText}`);
      }

      const data = await res.json();
      setTrainingDraft(data);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trainingDraft.status === 'in_progress' && trainingData?.training_content_type === 'scorm') {
      history.push(`/lms/start/training/${params?.backendId}/${params.airportCode.toUpperCase()}/${params.trainingId}`);
    }
  }, [trainingDraft, trainingData, params, history]);

  // methods for scratch training section
  const startTraining = (type) => {
    if (type === 'scorm') {
      history.push(`/lms/start/training/${params?.backendId}/${params.airportCode.toUpperCase()}/${params.trainingId}`);
      saveTrainingDraft({});
    } else {
      const data = {};
      let subSectionId = trainingData?.sections[0]?.contents[0]?.id;
      const quizzArray = trainingData?.sections?.map((obj, index) => ({
        id: obj.id,
        attempts: trainingDraft?.tracking_info?.attempts_used?.[index]?.attempts || 1,
        total_reattempts: Number(trainingData?.sections[index].additional_config?.no_of_quiz_reattempts) || 1,
        final_result: trainingDraft?.tracking_info?.attempts_used?.[index]?.final_result,
        current_attempt_result: trainingDraft?.tracking_info?.attempts_used?.[index]?.current_attempt_result?.length > 0
          && trainingDraft?.tracking_info?.attempts_used?.[index]?.current_attempt_result === 'fail' ? 'fail' : '',
        index
      }));
      data.tracking_info = {
        last_section_id: sectionId,
        last_content_id: subSectionId,
        last_page_number: 1,
        attempts_used: quizzArray
      };
      saveTrainingDraft(data);
      localStorage.setItem('attempt', 1);
      localStorage.setItem('result', '');
      history.push(`/lms/start/training/${params?.backendId}/${params.airportCode.toUpperCase()}/${params.trainingId}/${sectionId}/${subSectionId}`);
    }
  };

  // methods for scorm training type
  const countContents = (sections) => {
    let count = 0;

    const countRecursive = (items) => {
      items.forEach((item) => {
        count += item.contents ? item.contents.length : 0;
        if (item.contents && item.contents.length > 0) {
          countRecursive(item.contents);
        }
      });
    };

    countRecursive(sections);
    return count;
  };

  const renderSectionContents = section => section?.contents.map(item => (
    <div className={styles.sectionItem} key={item.identifier}>
      <div className={styles.sectionContent}>
        <h4>{item.title}</h4>
      </div>
    </div>
  ));

  const handleAnswerChanged = (answer, fieldId) => {
    if (answer.trim() === '' || answer === undefined) {
      handleFieldErrorChanged(fieldId, true);
    } else {
      setInfo(prevInfo => ({
        ...prevInfo,
        [fieldId]: answer
      }));
    }
  };

  const handleFieldErrorChanged = (id, value) => {
    setFieldErrors(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const handleSubmit = () => {
    const noErrors = Object.keys(fieldErrors).every(
      k => fieldErrors[k] === false
    );
    if (noErrors) {
      saveTrainingDraft(info);
    } else {
      setShowFormErrors(true);
    }
  };

  return (
    <>
      <Loading loadingStatus={loading} />
      {message === undefined && (
        <div className={styles.airportImage}>
          <img src={airportData?.image} alt="airport_logo" />
        </div>
      )}
      {!trainingDraft?.external_user_info?.first_name ? (
        <div className={styles.pageContainer}>
          <div className={styles.externalUser}>
            <div className={styles.formDetails}>
              <PulpoField
                key="first_name"
                id="first_name"
                type="string"
                title="First Name"
                translationID="forms.complete.name"
                handleValueChange={(a, b) => handleAnswerChanged(a, b)}
                answer={info.first_name || ''}
                className={styles.fullInput}
                isRequired
                handleFieldErrorChanged={handleFieldErrorChanged}
                showFieldErrors={showFormErrors}
              />
              <PulpoField
                key="last_name"
                id="last_name"
                type="string"
                title="Last Name"
                translationID="forms.complete.name"
                handleValueChange={(a, b) => handleAnswerChanged(a, b)}
                answer={info.last_name || ''}
                className={styles.fullInput}
                isRequired
                handleFieldErrorChanged={handleFieldErrorChanged}
                showFieldErrors={showFormErrors}
              />
            </div>
            <div className={styles.footer}>
              <div className={styles.stagebtn}>
                <Button
                  action="secondary"
                  onClick={() => handleSubmit()}
                  translationID="inspections.new.next"
                  defaultText="Next"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.trainingHeader}>
            {message ? (
              <h2 className={styles.errorMessage}>
                {message}
              </h2>
            ) : (
              <>
                <h2>{trainingData?.name}</h2>
                <p>{trainingData?.category}</p>
                <div>{trainingData?.description}</div>
              </>
            )}
          </div>
          {trainingData?.training_content_type === 'scorm' && (
            <div className={`${styles.trainingHeader} ${styles.mainblock}`}>
              <div
                className={styles.contentHeader}
              >
                <h2>{contentList?.organization_title || 'Training Content'}</h2>
                {trainingData?.status === 'active' && (
                  <Button
                    translationID="training.start.training"
                    defaultText="Start training"
                    onClick={() => startTraining('scorm')}
                  />
                )}
              </div>
              <div className={styles.totalInfo}>
                <p>
                  {contentList?.sections?.length || 0}
                  {' '}
                  Sections
                </p>
                <span className={styles.dot} />
                <p>
                  {contentList?.sections ? countContents(contentList?.sections) : 0}
                  {' '}
                  lectures
                </p>
              </div>
              <div>
                {contentList?.sections?.length > 0
                  && contentList?.sections?.map(section => (
                    <Collapsible
                      autoheight={false}
                      openOnMount
                      key={section?.identifier}
                      titleNode={
                        <>
                          <span className={styles.sectionText} key={section?.identifier}>
                            {section.title}
                          </span>
                          <div className={styles.totalInfo}>
                            <p>
                              {section?.contents?.length || 0}
                              {' '}
                              lectures
                            </p>
                          </div>
                        </>
                      }
                      styleClasses={styles.sectionCollaps}
                    >
                      {renderSectionContents(section)}
                    </Collapsible>
                  ))}
              </div>
            </div>
          )}
          {trainingData?.training_content_type === 'scratch' && (
            <SectionsList
              {...props}
              trainingData={trainingData}
              trainingDraft={trainingDraft}
              actiontTrainingDraft={getTrainingInfo}
              startTraining={startTraining}
              isPublic
            />
          )}
        </div>
      )}
    </>
  );
}

export default injectIntl(TrainingContentList);
